import { FC } from "react";
import styled from "styled-components";
import { cardSurface } from "../../components/atoms/Card";
import { formatDeadline, getUrgency, URGENCY_COLORS } from "./urgency";

/**
 * Shared primitives of the Suivi dashboard (home + group space + call sheet).
 * House style: flat 16px-radius cards with hairline separators, pill buttons,
 * status as small dots + words — never filled banners. Color budget: warm
 * secondary appears ONLY on "je saisis" CTAs; call actions are outline pills.
 */

export const Page = styled.div`
  padding: 16px 20px 90px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 720px;
  margin: 0 auto;
  width: 100%;
`;

export const SectionLabel = styled.p`
  margin: 6px 2px 0;
  font-size: 11.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.55);
`;

export const ListCard = styled.div`
  ${cardSurface}
  background: var(--app-color-surface-tint, white);
  overflow: hidden;
`;

export const Row = styled.div<{ $tappable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  min-height: 62px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  cursor: ${({ $tappable }) => ($tappable ? "pointer" : "default")};
  -webkit-tap-highlight-color: transparent;

  & + & {
    border-top: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.06);
  }
`;

export const RowMain = styled.div`
  flex: 1;
  min-width: 0;
`;

export const RowTitle = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--app-color-text, #0f172a);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowSub = styled.p`
  margin: 2px 0 0;
  font-size: 12.5px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const RowEnd = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  flex: none;
`;

export const Chevron = styled.span`
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.3);
  font-size: 20px;
  flex: none;
`;

/** Deadline chip: colored dot + relative text — never a filled background. */
const DueChipWrap = styled.span<{ $text: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $text }) => $text};
  white-space: nowrap;
`;

const DueDot = styled.i<{ $dot: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $dot }) => $dot};
  display: inline-block;
`;

export const DueChip: FC<{ endDate: string; t: (key: string, options?: Record<string, unknown>) => string }> = ({
  endDate,
  t,
}) => {
  const urgency = getUrgency(endDate);
  const colors = URGENCY_COLORS[urgency];
  return (
    <DueChipWrap $text={colors.text}>
      <DueDot $dot={colors.dot} />
      {formatDeadline(t, endDate)}
    </DueChipWrap>
  );
};

/** Status as words with a small dot (grey = not started, accent = in progress). */
export const StatusText = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $active }) =>
      $active ? "var(--app-color-emphasis, #2563eb)" : "rgba(var(--app-color-text-rgb, 15, 23, 42), 0.25)"};
  }
`;

/** Warm CTA — "je saisis". The ONLY place the secondary color appears. */
export const FillPill = styled.button`
  border: none;
  border-radius: 999px;
  min-height: 34px;
  padding: 0 15px;
  font-size: 13px;
  font-weight: 600;
  background: var(--app-color-secondary, #f59e0b);
  color: var(--app-color-secondary-contrast, #231a02);
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: var(--app-color-secondary-shade, #d9880a);
  }
`;

/** Outline pill — "je téléphone" (and other quiet actions). */
export const OutlinePill = styled.button`
  border: 1.5px solid rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.45);
  border-radius: 999px;
  min-height: 30px;
  padding: 0 12px;
  font-size: 12.5px;
  font-weight: 600;
  background: var(--app-color-background, #ffffff);
  color: var(--app-color-primary, #2563eb);
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.08);
  }
`;

export const QuietTag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 500;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.55);
  background: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.045);
  border-radius: 6px;
  padding: 2px 7px;
  white-space: nowrap;
`;

export const MemberInitial = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12.5px;
  background: var(--app-color-surface, #eff6ff);
  color: var(--app-color-primary, #2563eb);
`;

export const EmptyText = styled.p`
  text-align: center;
  padding: 32px 20px;
  font-size: 15px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);
`;

/** One quiet sentence — future windows are never cards (design contract). */
export const FooterLine = styled.p`
  margin: 0;
  text-align: center;
  font-size: 12.5px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.5);
`;

export const CaptionLine = styled.p`
  margin: 2px 2px 0;
  font-size: 13.5px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);

  b {
    color: var(--app-color-text, #0f172a);
    font-weight: 600;
  }
`;

/**
 * Ordinal-aware role label: with two+ same-role members (Outborn multiples), the
 * bare label is ambiguous — they become « Enfant 1 », « Enfant 2 » in creation
 * order. The rank comes from the SERVER (sibling_rank/sibling_count, computed
 * over the full family so every staff member numbers the same child identically,
 * whatever their site scope); the positional client computation is only a
 * fallback for payloads without the fields. KNOWN LIMIT: ranks are positional,
 * not persisted — removing an earlier data-empty sibling re-ranks the survivors
 * (which may already carry data). The fiche-collected name takes over as the
 * durable identifier as soon as it exists.
 */
export const memberRoleOrdinalLabel = (
  member: {
    user_id: number;
    study_role_id: number;
    role_label: string | null;
    sibling_rank?: number | null;
    sibling_count?: number | null;
  },
  allMembers: { user_id: number; study_role_id: number }[],
  fallback: string
): string => {
  const base = member.role_label ?? fallback;
  if (member.sibling_rank != null && member.sibling_count != null) {
    return member.sibling_count >= 2 ? `${base} ${member.sibling_rank}` : base;
  }
  const siblings = allMembers
    .filter((candidate) => candidate.study_role_id === member.study_role_id)
    .sort((first, second) => first.user_id - second.user_id);
  if (siblings.length < 2) return base;
  const rank = siblings.findIndex((candidate) => candidate.user_id === member.user_id) + 1;
  return rank > 0 ? `${base} ${rank}` : base;
};
